import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search as SearchIcon, LocationPulse } from '../components/Icon';
import { MapCanvas, type MapMarker } from '../components/MapCanvas';
import { ResultsPanel, type ParkingWithDistance } from '../components/ResultsPanel';
import { ResultsBottomSheet } from '../components/ResultsBottomSheet';
import { ParkingDetailSheet } from '../components/ParkingDetailSheet';
import { SubmitParkingFlow } from '../components/SubmitParkingFlow';
import {
  photonAutocomplete,
  placeTypeToRadiusKm,
  type PlaceType,
  type Suggestion,
} from '../services/photonAutocomplete';
import { haversineKm, withDistance, fetchParkingsInViewport } from '../services/parkings';
import { useAuth } from '../contexts/AuthContext';
import { useLoadout } from '../contexts/LoadoutContext';
import { useDocumentHead } from '../lib/seo';
import type { BBox } from '../services/parkings';

const AUTOCOMPLETE_DEBOUNCE_MS = 300;
const ZURICH_HB = { lat: 47.3779, lng: 8.5403 };

function bboxAroundKm(center: { lat: number; lng: number }, radiusKm: number): BBox {
  const dLat = radiusKm / 110.574;
  const dLng = radiusKm / (111.32 * Math.cos((center.lat * Math.PI) / 180));
  return [center.lat - dLat, center.lng - dLng, center.lat + dLat, center.lng + dLng];
}

function buildResultUrl(suggestion: Suggestion): string {
  const params = new URLSearchParams();
  params.set('q', suggestion.displayName);
  params.set('lat', String(suggestion.lat));
  params.set('lng', String(suggestion.lng));
  params.set('placeType', suggestion.placeType);
  return `/?${params.toString()}`;
}

const HOW_IT_WORKS_STEPS = [
  {
    title: "Type where you're going",
    body: 'A restaurant, an address, a landmark — anywhere.',
  },
  {
    title: 'See parkings around it',
    body: 'Clearance heights from OpenStreetMap, plus driver-verified measurements.',
  },
  {
    title: 'Pick your car',
    body: 'Get a green ✓ or red ✗ verdict on every parking, instantly.',
  },
];

/** Shared search bar UI */
function SearchBar({
  query,
  onQueryChange,
  onKeyDown,
  suggestions,
  suggestionsOpen,
  onPickSuggestion,
  autoFocus,
}: {
  query: string;
  onQueryChange: (v: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  suggestions: Suggestion[];
  suggestionsOpen: boolean;
  onPickSuggestion: (s: Suggestion) => void;
  autoFocus?: boolean;
}) {
  return (
    <div className="relative w-full">
      <div className="flex items-center gap-2.5 px-s4 py-3.5 bg-card border border-rule rounded-pill shadow-sm">
        <SearchIcon size={16} color="#8B8680" />
        <input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Restaurant, address, landmark…"
          autoCorrect="off"
          autoCapitalize="off"
          autoFocus={autoFocus}
          className="flex-1 bg-transparent text-[16px] font-sans text-ink1 placeholder-ink3 outline-none"
        />
      </div>

      {suggestionsOpen && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] bg-card border border-rule rounded-lg shadow-lg overflow-hidden z-30">
          {suggestions.map((s, i) => (
            <button
              key={`${s.lat}-${s.lng}-${i}`}
              type="button"
              onClick={() => onPickSuggestion(s)}
              className="w-full text-left px-s4 py-s3 hover:bg-tint transition-colors border-b border-rule-soft last:border-b-0"
            >
              <div className="text-[14px] font-sans font-medium text-ink1 truncate">{s.name}</div>
              <div className="text-[12px] font-sans text-ink3 truncate">{s.displayName}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Autocomplete hook - returns suggestions as user types */
function useAutocomplete() {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleQueryChange = useCallback((value: string) => {
    console.log('[Autocomplete] Query changed:', value);
    setQuery(value);
    
    // Clear pending requests
    if (debounceRef.current) clearTimeout(debounceRef.current);
    abortRef.current?.abort();

    if (value.trim().length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      
      try {
        console.log('[Autocomplete] Fetching suggestions for:', value);
        const results = await photonAutocomplete(value, null, controller.signal);
        console.log('[Autocomplete] Got', results.length, 'suggestions');
        setSuggestions(results);
        setIsOpen(true);
      } catch (err) {
        // Aborted or error - ignore
      }
    }, AUTOCOMPLETE_DEBOUNCE_MS);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, []);

  return {
    query,
    setQuery,
    suggestions,
    isOpen,
    setIsOpen,
    handleQueryChange,
  };
}

/** Empty home state - just the search box */
function EmptyHomeState() {
  const navigate = useNavigate();
  const { query, suggestions, isOpen, setIsOpen, handleQueryChange } = useAutocomplete();

  useDocumentHead({
    title: 'ParkProof — Will my car fit? Parking heights for tall cars, SUVs & vans',
    description: 'Search any destination in Europe and see driver-verified parking clearance heights around it.',
    canonicalPath: '/',
  });

  const pickSuggestion = useCallback((suggestion: Suggestion) => {
    console.log('[EmptyHome] Picked suggestion:', suggestion.name);
    setIsOpen(false);
    navigate(buildResultUrl(suggestion));
  }, [navigate, setIsOpen]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && suggestions.length > 0) {
      e.preventDefault();
      pickSuggestion(suggestions[0]);
    }
  }, [suggestions, pickSuggestion]);

  return (
    <div className="flex flex-col flex-1 p-s5 md:pt-s7 pb-s7 gap-s6 items-center">
      <div className="w-full max-w-[560px] flex flex-col gap-s6">
        <div className="flex flex-col items-center text-center gap-s2 md:mt-s4">
          <h1 className="font-serif text-[34px] md:text-[44px] leading-[38px] md:leading-[48px] tracking-tight text-ink1">
            Where are you <span className="font-serif-italic">going?</span>
          </h1>
          <p className="text-[14px] md:text-[15px] text-ink2 font-sans leading-snug max-w-[420px]">
            Type a destination — we'll show parking garages around it, with clearance heights for your car.
          </p>
        </div>

        <SearchBar
          query={query}
          onQueryChange={handleQueryChange}
          onKeyDown={onKeyDown}
          suggestions={suggestions}
          suggestionsOpen={isOpen}
          onPickSuggestion={pickSuggestion}
          autoFocus
        />

        <div className="flex flex-col gap-s4 mt-s3">
          <span className="caption text-ink3 text-center">How ParkProof works</span>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-s4">
            {HOW_IT_WORKS_STEPS.map((step, i) => (
              <div key={step.title} className="flex flex-col items-center text-center gap-1.5 p-s4">
                <div className="w-7 h-7 rounded-pill border border-rule bg-card flex items-center justify-center font-mono text-[12px] text-ink2 mb-1">
                  {i + 1}
                </div>
                <div className="text-[13px] font-sans font-semibold text-ink1">{step.title}</div>
                <div className="text-[12px] font-sans text-ink3 leading-snug">{step.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Result state - map + parking list */
function ResultHomeState({
  q,
  lat,
  lng,
  placeType,
  initialZoom,
  parkingId,
}: {
  q: string;
  lat: number;
  lng: number;
  placeType: PlaceType | null;
  initialZoom: number;
  parkingId?: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { effectiveCars } = useAuth();
  const { effectiveHeight } = useLoadout();
  const car = effectiveCars[0];
  const totalHeight = effectiveHeight(car);

  // Search bar state
  const { query, setQuery, suggestions, isOpen, setIsOpen, handleQueryChange } = useAutocomplete();
  
  // Initialize query from URL
  useEffect(() => {
    setQuery(q);
  }, [q, setQuery]);

  // Calculate initial bbox from URL params - this only runs once per lat/lng change
  const radiusKm = placeTypeToRadiusKm(placeType ?? 'other');
  const searchBbox = useMemo(() => {
    console.log('[ResultHome] Computing bbox for lat:', lat, 'lng:', lng, 'radius:', radiusKm);
    return bboxAroundKm({ lat, lng }, radiusKm);
  }, [lat, lng, radiusKm]);

  // Query parkings - this ONLY re-runs when searchBbox changes
  const bboxKey = searchBbox.map(n => n.toFixed(4)).join(',');
  const parkingsQuery = useQuery({
    queryKey: ['parkings', bboxKey],
    queryFn: async () => {
      console.log('[ResultHome] Fetching parkings for bbox:', bboxKey);
      const result = await fetchParkingsInViewport(searchBbox);
      console.log('[ResultHome] Got', result.length, 'parkings');
      return result;
    },
    staleTime: 60 * 1000, // 1 minute
  });

  const parkings = parkingsQuery.data ?? [];

  // Map center - must update when lat/lng props change
  // Using the props directly, not state, since we want it to always reflect URL params
  const mapCenter = useMemo(() => {
    console.log('[ResultHome] Map center updated to:', lat, lng);
    return { lat, lng };
  }, [lat, lng]);
  
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [showSubmitFlow, setShowSubmitFlow] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  // Computed values - deduplicate by id to avoid React key warnings
  const withDistanceList = useMemo(() => {
    const seen = new Set<string>();
    const dedupedParkings = parkings.filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
    return withDistance(dedupedParkings, { lat, lng }) as ParkingWithDistance[];
  }, [parkings, lat, lng]);

  const markers: MapMarker[] = useMemo(() => {
    const seen = new Set<string>();
    return parkings.filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    }).map((p) => ({
      id: p.id,
      lat: p.lat,
      lng: p.lng,
      tier: p.confidenceTier ?? 'unknown',
    }));
  }, [parkings]);

  useDocumentHead({
    title: `${q} — parking near you · ParkProof`,
    description: `Driver-verified parking clearance heights around ${q}. Built for SUVs, vans, pickups, and cars with roof boxes.`,
    canonicalPath: `/?q=${encodeURIComponent(q)}`,
  });

  const pickSuggestion = useCallback((suggestion: Suggestion) => {
    console.log('[ResultHome] Picked new suggestion:', suggestion.name);
    setIsOpen(false);
    navigate(buildResultUrl(suggestion));
  }, [navigate, setIsOpen]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && suggestions.length > 0) {
      e.preventDefault();
      pickSuggestion(suggestions[0]);
    }
  }, [suggestions, pickSuggestion]);

  // Handle "Search this area" button click
  const handleSearchThisArea = useCallback((newCenter: { lat: number; lng: number }, newZoom: number) => {
    console.log('[ResultHome] Search this area clicked:', newCenter, 'zoom:', newZoom);
    const params = new URLSearchParams();
    params.set('q', 'Map area');
    params.set('lat', String(newCenter.lat));
    params.set('lng', String(newCenter.lng));
    params.set('placeType', 'other');
    params.set('zoom', String(Math.round(newZoom)));
    navigate(`/?${params.toString()}`);
  }, [navigate]);

  return (
    <div className="flex flex-col flex-1 md:flex-row min-h-0">
      {/* Mobile: floating search bar */}
      <div className="md:hidden absolute top-0 left-0 right-0 z-20 p-s4 bg-page/95 backdrop-blur">
        <SearchBar
          query={query}
          onQueryChange={handleQueryChange}
          onKeyDown={onKeyDown}
          suggestions={suggestions}
          suggestionsOpen={isOpen}
          onPickSuggestion={pickSuggestion}
        />
      </div>

      {/* Desktop: left panel */}
      <div className="hidden md:flex md:flex-col md:w-[400px] md:border-r md:border-rule overflow-hidden">
        <div className="p-s4 border-b border-rule bg-page">
          <SearchBar
            query={query}
            onQueryChange={handleQueryChange}
            onKeyDown={onKeyDown}
            suggestions={suggestions}
            suggestionsOpen={isOpen}
            onPickSuggestion={pickSuggestion}
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          {parkingsQuery.isLoading ? (
            <div className="flex items-center justify-center h-full p-s5">
              <p className="text-ink3 text-[14px]">Loading parkings...</p>
            </div>
          ) : parkings.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-s5 text-center">
              {submissionSuccess ? (
                <>
                  <p className="text-green-600 text-[14px] font-medium">Thanks for your submission!</p>
                  <p className="text-ink3 text-[13px] mt-1">We'll review it and add it to the map.</p>
                </>
              ) : (
                <>
                  <p className="text-ink2 text-[14px]">No parkings found in this area</p>
                  <button
                    onClick={() => setShowSubmitFlow(true)}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    + Add a parking here
                  </button>
                </>
              )}
            </div>
          ) : (
            <ResultsPanel
              parkings={withDistanceList}
              carHeightM={totalHeight}
              highlightedId={highlightedId}
              onHoverItem={setHighlightedId}
            />
          )}
        </div>
      </div>

      {/* Map */}
      <div className="relative flex-1 h-[50vh] md:h-screen md:sticky md:top-0 pt-[76px] md:pt-0">
        <MapCanvas
          center={mapCenter}
          zoom={initialZoom}
          markers={markers}
          searchLocation={{ lat, lng }}
          highlightedMarkerId={highlightedId}
          onMarkerClick={(id) => navigate(`/parking/${id}${location.search}`)}
          onSearchThisArea={handleSearchThisArea}
          className="w-full h-full"
        />
      </div>

      {/* Mobile bottom sheet */}
      {!parkingId && (
        <ResultsBottomSheet
          parkings={withDistanceList}
          carHeightM={totalHeight}
          highlightedId={highlightedId}
        />
      )}

      {/* Parking detail sheet */}
      {parkingId && (
        <ParkingDetailSheet
          parkingId={parkingId}
          onClose={() => navigate(`/${location.search}`)}
        />
      )}

      {/* Submit parking flow */}
      {showSubmitFlow && (
        <SubmitParkingFlow
          lat={lat}
          lng={lng}
          onClose={() => setShowSubmitFlow(false)}
          onSuccess={() => {
            setShowSubmitFlow(false);
            setSubmissionSuccess(true);
          }}
        />
      )}
    </div>
  );
}

/** Main HomePage component - routes to empty or result state */
export function HomePage() {
  const [searchParams] = useSearchParams();
  const { parkingId, citySlug } = useParams();
  const navigate = useNavigate();
  const [cityLookupLoaded, setCityLookupLoaded] = useState(false);
  const [cityData, setCityData] = useState<{ name: string; lat: number; lng: number } | null>(null);
  
  const q = searchParams.get('q') ?? '';
  const latStr = searchParams.get('lat');
  const lngStr = searchParams.get('lng');
  const placeType = searchParams.get('placeType') as PlaceType | null;
  const zoomStr = searchParams.get('zoom');

  const lat = latStr ? parseFloat(latStr) : null;
  const lng = lngStr ? parseFloat(lngStr) : null;
  const zoom = zoomStr ? parseFloat(zoomStr) : 15; // Default zoom is 15

  // Handle city slug - look up coordinates from city-lookup.json
  useEffect(() => {
    if (citySlug && !lat && !lng) {
      fetch('/city-lookup.json')
        .then(res => res.json())
        .then((lookup: Record<string, [string, number, number]>) => {
          const city = lookup[citySlug];
          if (city) {
            const [name, cityLat, cityLng] = city;
            setCityData({ name, lat: cityLat, lng: cityLng });
          }
          setCityLookupLoaded(true);
        })
        .catch(() => setCityLookupLoaded(true));
    }
  }, [citySlug, lat, lng]);

  // If we have a city slug and loaded city data, redirect to search
  useEffect(() => {
    if (cityData && citySlug) {
      const params = new URLSearchParams();
      params.set('q', cityData.name);
      params.set('lat', String(cityData.lat));
      params.set('lng', String(cityData.lng));
      params.set('placeType', 'city');
      navigate(`/?${params.toString()}`, { replace: true });
    }
  }, [cityData, citySlug, navigate]);

  console.log('[HomePage] Rendering with q:', q, 'lat:', lat, 'lng:', lng, 'zoom:', zoom, 'citySlug:', citySlug);

  // Show loading while fetching city data
  if (citySlug && !cityLookupLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <p className="text-ink3">Loading...</p>
      </div>
    );
  }

  // If we have coordinates, show results; otherwise show empty state
  if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
    // Key forces remount when location changes, ensuring fresh state
    const locationKey = `${lat.toFixed(4)}-${lng.toFixed(4)}-${zoom}`;
    return (
      <ResultHomeState
        key={locationKey}
        q={q}
        lat={lat}
        lng={lng}
        placeType={placeType}
        initialZoom={zoom}
        parkingId={parkingId}
      />
    );
  }

  return <EmptyHomeState />;
}

export default HomePage;
